import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { DateValueType } from "../../DatepickerV2";
import type { ChildDisplayStatus, SubIB, SubIBHoid } from "../types";

import { Button as ButtonV2 } from "../../Button";
import Datepicker from "../../DatepickerV2";
import { getShortcutButtonLabel } from "../../DatepickerV2/helpers/shortcutLabel";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useDatepickerTranslations } from "../../hooks/useDatepickerTranslations";
import {
  AlertTriangleIcon,
  ArrowDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CrossIcon,
  EditIcon,
  ErrorIconV2,
  InfoIcon,
  MailIcon,
  PlusIcon,
} from "../../Icons";
import { InputV2 } from "../../InputV2";
import { SkeletonLoader } from "../../SkeletonLoader";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import {
  COMMENT_CHARACTER_LIMIT,
  EMPTY_DATE,
  SAVE_ERROR_MESSAGE,
} from "../consts";
import { getDefaultDateValue } from "../providers/getDefaultDateRange";
import { StatusPill } from "./StatusPill";

type SubIBDetailDrawerProps = {
  open: boolean;
  row: SubIB | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onClose: () => void;
  /** Design change #2 (PART-3930): may reject to surface an inline save error. */
  onSaveComment: (row: SubIB, value: string) => void | Promise<void>;
  /**
   * Design change #3 (PART-3930): may reject to surface an inline save error.
   * Omitted → the permission toggle commits locally and always succeeds, which
   * is what Storybook / vibe-coding / tests want.
   */
  onSavePermissions?: (
    row: SubIB,
    seeNameAndEmail: boolean,
  ) => void | Promise<void>;
  onAddProgram: (row: SubIB) => void;
  onViewProgram: (hoid: SubIBHoid) => void;
  /**
   * Present when this drawer replaced another one — e.g. opened from a Sub IB
   * name in the Program details drawer's Rebate split. Renders the leading back
   * affordance the canvas shows in that case (`ProgramDrawer`'s `drawer__back`,
   * spec §7.3 "replace + back"); omitted when opened straight from the table.
   */
  onBack?: () => void;
};

const fmtDate = (d: string | null): string => {
  if (!d) return EMPTY_DATE;
  return new Date(d).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtUsd = (n: number): string =>
  n > 0
    ? "US$" +
      n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "US$0";

const fmtInt = (n: number): string => n.toLocaleString("en-US");

// Below 1 million, trading volume is shown as an exact dollar figure (e.g.
// "9,000 USD"); at or above 1 million it collapses to the "mln USD"
// convention with 2 decimal places (e.g. "1.29 mln USD"); at or above 1
// billion it collapses further to "bln USD" (e.g. "1.29 bln USD") —
// matching ../rebate-programs/data.ts's fmtVolMln (duplicated here, not
// imported — see that file for the reasoning).
const fmtVolMln = (n: number): string => {
  if (Math.abs(n) < 1e6) {
    return `${n.toLocaleString("en-US", { maximumFractionDigits: 0 })} USD`;
  }
  if (Math.abs(n) < 1e9) {
    return (
      (n / 1e6).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " mln USD"
    );
  }
  return (
    (n / 1e9).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " bln USD"
  );
};

const hoidDisplayStatus = (
  hoid: SubIBHoid,
  parentKycPending: boolean,
): ChildDisplayStatus => {
  if (hoid.status === "archived") return "archived";
  if (parentKycPending) return "pendingKyc";
  return "active";
};

const KvLabel = ({ label, info }: { label: string; info?: string }) => (
  <span className="inline-flex items-center gap-1">
    <Typo
      element="span"
      type="labelXS"
      textColor="text-text-tertiary"
      className="uppercase tracking-[0.04em]"
    >
      {label}
    </Typo>
    {info && (
      <TooltipWrapper
        cursor="help"
        text={info}
        color="blue"
        // Matches the Program details drawer — see the note on its `KV`.
        tooltipPlacement="top"
        tooltipMaxWidth={240}
        tooltipContainerClassName="!text-xs"
      >
        <InfoIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
      </TooltipWrapper>
    )}
  </span>
);

const KV = ({
  label,
  info,
  children,
  subValue,
  valueColor = "text-text-primary",
}: {
  label: string;
  info?: string;
  children: React.ReactNode;
  subValue?: string;
  /**
   * Overrides the value's colour. Used to dim an absent date's `--` to
   * `text-text-disabled`, per the canvas's `.dash` rule.
   */
  valueColor?: string;
}) => (
  <div className="flex min-w-0 flex-col gap-2">
    <KvLabel label={label} info={info} />
    <span className="flex min-w-0 items-baseline gap-2">
      <Typo
        element="span"
        type="bodyMD"
        textColor={valueColor}
        className="break-words"
      >
        {children}
      </Typo>
      {subValue && (
        <Typo element="span" type="labelXS" textColor="text-text-secondary">
          {subValue}
        </Typo>
      )}
    </span>
  </div>
);

const CommentSection = ({
  comment,
  onSave,
}: {
  comment: string;
  /** Design change #2 (PART-3930): may reject — see EditCommentModal's onSave. */
  onSave: (value: string) => void | Promise<void>;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const empty = !comment || !comment.trim();

  // Replaces the old textarea's `autoFocus`: the editor mounts fresh each time
  // `editing` flips, so focusing on that transition matches the prior behaviour.
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // Design change #2: a failed save keeps the editor open with an inline error
  // and leaves the committed comment untouched; only success closes it.
  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      setError(SAVE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <KvLabel label="Comment" />
        <div className="flex flex-col gap-3">
          {/* Design change #1 (PART-3930): single-line InputV2 at 60 chars.
              The KvLabel above already names the field. */}
          <InputV2
            ref={inputRef}
            name="sub-ib-drawer-comment"
            value={draft}
            characterLimit={COMMENT_CHARACTER_LIMIT}
            onChange={(next) => {
              setDraft(next);
              setError("");
            }}
            error={error}
            ariaLabel="Edit comment"
          />
          <div className="flex justify-end gap-2">
            <ButtonV2
              variant="tonal"
              size="sm"
              disabled={saving}
              onClick={() => {
                setDraft(comment);
                setError("");
                setEditing(false);
              }}
            >
              Cancel
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="sm"
              isLoading={saving}
              onClick={handleSave}
            >
              Save
            </ButtonV2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <KvLabel label="Comment" />
        <button
          type="button"
          onClick={() => {
            setDraft(comment);
            setEditing(true);
          }}
          className="inline-flex items-center gap-1 text-text-brand-default hover:text-text-brand-bold hover:underline"
        >
          <EditIcon className="h-3 w-3" />
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-brand-default"
          >
            Edit
          </Typo>
        </button>
      </div>
      <Typo
        element="p"
        type="bodyMD"
        textColor={empty ? "text-text-tertiary" : "text-text-primary"}
        className={empty ? "italic" : "whitespace-pre-wrap"}
      >
        {empty ? "No comment yet." : comment}
      </Typo>
    </div>
  );
};

const DateRangeChip = ({
  value,
  onChange,
}: {
  value: DateValueType;
  onChange: (v: DateValueType) => void;
}) => {
  const configs = useDatepickerTranslations();
  // With a real seeded value the trigger would otherwise show two formatted
  // dates; this collapses an exact preset match back to "Last 30 days".
  const shortcutLabel = getShortcutButtonLabel(value, configs);

  return (
    <Datepicker
      value={value}
      onChange={onChange}
      useRange
      showShortcuts
      showFooter
      configs={configs}
      displayFormat="DD MMM YYYY"
      separator=" - "
      // The drawer's body scrolls (overflow-y-auto), which per the CSS spec
      // also forces overflow-x to clip — DatepickerV2's popup is a plain
      // `absolute` div (no portal), so it gets cut off there. `!fixed`
      // escapes that ancestor clipping entirely; `right-0` still resolves
      // correctly since this drawer sits flush against the viewport's own
      // right edge.
      popupClassName={(defaultClassName) => `${defaultClassName} !fixed`}
      trigger={({ open, inputText, isEmpty }) => (
        <button
          type="button"
          onClick={open}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-stroke-primary px-3 py-1.5 hover:bg-surface-tonal-default"
        >
          <Typo element="span" type="labelXS" textColor="text-text-primary">
            {shortcutLabel ?? (isEmpty ? "Last 30 days" : inputText)}
          </Typo>
          <ArrowDownIcon className="h-3 w-3 text-text-primary" />
        </button>
      )}
    />
  );
};

const ProgramOverviewCard = ({
  hoid,
  subjectSubId,
  parentKycPending,
  onView,
}: {
  hoid: SubIBHoid;
  subjectSubId: string;
  parentKycPending: boolean;
  onView: () => void;
}) => {
  const displayStatus = hoidDisplayStatus(hoid, parentKycPending);
  const coPartner = hoid.partners.find((p) => p.subIbId !== subjectSubId);

  return (
    <div className="overflow-hidden rounded-lg border border-stroke-primary">
      <button
        type="button"
        onClick={onView}
        className="flex min-h-14 w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-overlay-opacity-state-hover"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <Typo element="span" type="labelSM" textColor="text-text-primary">
              {hoid.rebateProgramLabel}
            </Typo>
            <Typo
              element="span"
              type="labelSM"
              textColor="text-text-primary"
              aria-hidden="true"
            >
              ·
            </Typo>
            <Typo element="span" type="labelSM" textColor="text-text-primary">
              {hoid.rebateSplitPct}% split
            </Typo>
            <StatusPill status={displayStatus} />
          </span>
          {coPartner && (
            <span className="flex items-center gap-1.5">
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-secondary"
              >
                Shared with
              </Typo>
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-secondary"
              >
                {coPartner.name}
              </Typo>
            </span>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-1">
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-brand-default"
          >
            View program detail
          </Typo>
          <ChevronRightIcon className="h-3.5 w-3.5 text-text-brand-default" />
        </span>
      </button>
    </div>
  );
};

const PermissionToggleRow = ({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) => (
  <div className="flex flex-col gap-2">
    <KvLabel
      label="See your name and email"
      info="When on, the Sub IB can see your name and email."
    />
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label="See your name and email"
        onClick={onToggle}
        className={`relative inline-flex h-6 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
          checked ? "bg-surface-brand-default" : "bg-stroke-secondary"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-[26px]" : "translate-x-[2px]"
          }`}
        />
      </button>
      <Typo element="span" type="bodyMD" textColor="text-text-primary">
        {checked ? "On" : "Off"}
      </Typo>
    </div>
  </div>
);

const DrawerSkeletonBody = () => (
  <div className="flex flex-col gap-8 p-8" data-testid="drawer-loading">
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <SkeletonLoader height={20} width={160} variant="rounded" />
        <SkeletonLoader height={16} width={224} variant="rounded" />
      </div>
      <div className="grid grid-cols-3 gap-6">
        <SkeletonLoader height={40} variant="rounded" />
        <SkeletonLoader height={40} variant="rounded" />
        <SkeletonLoader height={40} variant="rounded" />
      </div>
      <SkeletonLoader height={64} variant="rounded" />
    </section>
    <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
      <SkeletonLoader height={20} width={192} variant="rounded" />
      <div className="grid grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonLoader key={i} height={40} variant="rounded" />
        ))}
      </div>
    </section>
    <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
      <SkeletonLoader height={20} width={160} variant="rounded" />
      <SkeletonLoader height={56} variant="rounded" />
      <SkeletonLoader height={56} variant="rounded" />
    </section>
  </div>
);

const DrawerErrorBody = ({ onRetry }: { onRetry: () => void }) => (
  <div
    className="flex h-full flex-col items-center justify-center gap-6 p-8 text-center"
    data-testid="drawer-error"
  >
    <ErrorIconV2 width={64} height={64} />
    <div className="flex flex-col items-center gap-2">
      <Typo element="span" type="labelLG" textColor="text-text-primary">
        Unable to load data
      </Typo>
      <Typo element="span" type="bodyMD" textColor="text-text-secondary">
        Refresh to try again
      </Typo>
    </div>
    <ButtonV2 variant="brand" size="md" onClick={onRetry}>
      Refresh
    </ButtonV2>
  </div>
);

const DrawerContent = ({
  row,
  onSaveComment,
  onSavePermissions,
  onAddProgram,
  onViewProgram,
}: {
  row: SubIB;
  onSaveComment: (value: string) => void | Promise<void>;
  /** Design change #3 (PART-3930): may reject — nothing commits on failure. */
  onSavePermissions?: (seeNameAndEmail: boolean) => void | Promise<void>;
  onAddProgram: () => void;
  onViewProgram: (hoid: SubIBHoid) => void;
}) => {
  const [metricsRange, setMetricsRange] =
    useState<DateValueType>(getDefaultDateValue);
  const [permOn, setPermOn] = useState(false);
  const [savedPermOn, setSavedPermOn] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [permSaving, setPermSaving] = useState(false);
  const [permError, setPermError] = useState("");

  const permDirty = permOn !== savedPermOn;
  const isPendingKyc = row.kycStatus === "pending";
  const programCount = row.hoids.length;
  const sharedCount = row.hoids.filter((h) => h.partners.length > 1).length;
  const hoidsSorted = [...row.hoids].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const togglePerm = () => {
    setJustSaved(false);
    // Design change #3: toggling further clears a failed-save error.
    setPermError("");
    setPermOn((v) => !v);
  };

  // Design change #3: on failure nothing commits (`savedPermOn` is untouched, so
  // the toggles keep their unsaved state) and no "Saved" confirmation shows.
  const savePerm = async () => {
    setPermSaving(true);
    setPermError("");
    try {
      await onSavePermissions?.(permOn);
      setSavedPermOn(permOn);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 3000);
    } catch {
      setPermError(SAVE_ERROR_MESSAGE);
    } finally {
      setPermSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-8 p-8">
      {/* === Personal information === */}
      <section className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="flex flex-wrap items-center gap-3">
            <Typo element="span" type="labelLG" textColor="text-text-primary">
              {row.name}
            </Typo>
            <StatusPill
              status={
                isPendingKyc
                  ? "pendingKyc"
                  : row.aggregateStatus === "active"
                    ? "active"
                    : "inactive"
              }
            />
          </span>
          <span className="flex items-center gap-2">
            <MailIcon className="h-4 w-4 shrink-0 text-icon-secondary" />
            <Typo element="span" type="bodyMD" textColor="text-text-secondary">
              {row.email}
            </Typo>
          </span>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <KV label="Partner ID">{row.subRef}</KV>
          <KV
            label="Sub IB Since"
            valueColor={row.addedAt ? undefined : "text-text-disabled"}
          >
            {fmtDate(row.addedAt)}
          </KV>
          <KV
            label="Last payment"
            info="Most recent payment date across all rebate programs."
            // Absent date → the `--` dims, per the canvas's `.dash`.
            valueColor={row.lastPayment ? undefined : "text-text-disabled"}
          >
            {fmtDate(row.lastPayment)}
          </KV>
        </div>

        <CommentSection comment={row.comment} onSave={onSaveComment} />
      </section>

      {/* === Aggregated metrics === */}
      <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Typo
              element="h3"
              type="labelMD"
              textColor="text-text-primary"
              className="whitespace-nowrap"
            >
              Aggregated metrics
            </Typo>
            <Typo element="p" type="bodySM" textColor="text-text-tertiary">
              Based on {programCount} rebate program
              {programCount === 1 ? "" : "s"}
              {sharedCount > 0 && ", figure shared with other Sub IB"}
            </Typo>
          </div>
          {/* DatepickerV2's own root wrapper renders with `w-full`, which as a
              direct flex child would fight the title block for width and
              squeeze it — this wrapper absorbs that instead. */}
          <div className="shrink-0">
            <DateRangeChip value={metricsRange} onChange={setMetricsRange} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <KV
            label="Total earning"
            info="Gross rebate generated across all rebate programs."
          >
            {fmtUsd(row.totalRebate)}
          </KV>
          <KV label="Your payout">{fmtUsd(row.myPayout)}</KV>
          <KV label="Sub IB payout">{fmtUsd(row.aggregateSubIbPayout)}</KV>
          <KV
            label="Clients"
            info="All-time client data across this Sub IB's rebate programs, regardless of the selected date range."
          >
            {fmtInt(row.aggregateUniqueClients)}
          </KV>
          <KV
            label="Client accounts"
            info="All-time client data across this Sub IB's rebate programs, regardless of the selected date range."
          >
            {fmtInt(row.clientAccounts)}
          </KV>
          <KV label="Trading volume">{fmtVolMln(row.tradingVolume)}</KV>
        </div>
      </section>

      {/* === Rebate programs === */}
      <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Typo element="h3" type="labelMD" textColor="text-text-primary">
              Rebate programs
            </Typo>
            <Typo element="p" type="bodySM" textColor="text-text-tertiary">
              {programCount} program{programCount === 1 ? "" : "s"}
            </Typo>
          </div>
          <ButtonV2 variant="outline" size="sm" onClick={onAddProgram}>
            <div className="flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              Add new program
            </div>
          </ButtonV2>
        </div>
        <div className="flex flex-col gap-3">
          {hoidsSorted.map((h) => (
            <ProgramOverviewCard
              key={h.id}
              hoid={h}
              subjectSubId={row.id}
              parentKycPending={isPendingKyc}
              onView={() => onViewProgram(h)}
            />
          ))}
        </div>
      </section>

      {/* === Permission settings === */}
      <section className="flex flex-col gap-4 border-t border-stroke-primary pt-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Typo element="h3" type="labelMD" textColor="text-text-primary">
              Permission settings
            </Typo>
            <Typo element="p" type="bodySM" textColor="text-text-tertiary">
              Control what {row.name} can see. Save to apply your changes.
            </Typo>
          </div>
          {permDirty ? (
            <ButtonV2
              variant="brand"
              size="sm"
              isLoading={permSaving}
              onClick={savePerm}
            >
              Save
            </ButtonV2>
          ) : (
            justSaved && (
              <span className="inline-flex items-center gap-1.5 text-text-positive-bold">
                <CheckIcon className="h-3.5 w-3.5" />
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-positive-bold"
                >
                  Saved
                </Typo>
              </span>
            )
          )}
        </div>
        {/* No `gap` on this wrapper: it keeps the toggle block and the error
            line as one child of the section, so the section's own 16px gap
            can't get between them and the error can sit at the 4px the design
            specifies. */}
        <div className="flex flex-col">
          <div className="flex flex-col gap-5">
            <PermissionToggleRow checked={permOn} onToggle={togglePerm} />
          </div>
          {/* Design change #3 (PART-3930): failed-save line, 4px below the
              toggle row, announced live. Stays mounted and empty so appearing
              text is announced without shifting layout when there's no error. */}
          <div aria-live="polite">
            {permError && (
              <span className="mt-1 flex items-center gap-1 text-text-error">
                <AlertTriangleIcon
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                  focusable="false"
                />
                <Typo element="span" type="bodySM" textColor="text-text-error">
                  {permError}
                </Typo>
              </span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export const SubIBDetailDrawer = ({
  open,
  row,
  isLoading,
  isError,
  onRetry,
  onClose,
  onSaveComment,
  onSavePermissions,
  onAddProgram,
  onViewProgram,
  onBack,
}: SubIBDetailDrawerProps) => {
  useBodyScrollLock(open);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-surface-tonal-boldest"
        onClick={onClose}
        aria-hidden="true"
        data-testid="drawer-backdrop"
      />

      {/* Drawer panel */}
      <div
        className="absolute right-0 top-0 flex h-full w-[680px] max-w-full flex-col bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Sub IB Details"
        data-testid="sub-ib-detail-drawer"
      >
        <div className="sticky top-0 z-[1] flex items-center justify-between gap-4 border-b border-stroke-primary bg-white px-8 py-6">
          <div className="flex min-w-0 items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary hover:bg-overlay-opacity-state-hover"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
            <Typo element="h2" type="headingLG" textColor="text-text-primary">
              Sub IB Details
            </Typo>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-icon-secondary hover:bg-overlay-opacity-state-hover"
          >
            <CrossIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isError ? (
            <DrawerErrorBody onRetry={onRetry} />
          ) : isLoading || !row ? (
            <DrawerSkeletonBody />
          ) : (
            <DrawerContent
              key={row.id}
              row={row}
              onSaveComment={(value) => onSaveComment(row, value)}
              onSavePermissions={
                onSavePermissions
                  ? (seeNameAndEmail) => onSavePermissions(row, seeNameAndEmail)
                  : undefined
              }
              onAddProgram={() => onAddProgram(row)}
              onViewProgram={onViewProgram}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
